import { useEffect, useMemo, useState } from "react";
import { IconFilter } from "@tabler/icons-react";
import { Button, Group, PaginadorTabla, Stack, TablaSkeleton } from "@/components/ui";
import { useDebouncedValue } from "@/lib/hooks/use-debounced-value";
import type { BandejaAutorizacionItem } from "@/lib/types";
import { listarBandejaAutorizacion } from "../services/bandeja-service";
import { BANDEJA_PAGE_SIZE, useBandejaAutorizacion } from "../hooks/use-bandeja-autorizacion";
import { useNotificarLlegada } from "../hooks/use-notificar-llegada";
import { filtrosNoVacios, type BandejaFiltros } from "../utils/ordenar-bandeja";
import { BandejaHeader } from "./BandejaHeader";
import { BandejaDesktopTable, COLUMNAS_BANDEJA, MIN_WIDTH_BANDEJA } from "./BandejaDesktopTable";
import { BandejaMobileList } from "./BandejaMobileList";
import { BandejaVaciaCard } from "./BandejaEmptyState";
import { BandejaAccionMasiva } from "./BandejaAccionMasiva";

/**
 * Página de la bandeja de autorización · listado con polling. El filtrado es por
 * columna (fila de búsqueda bajo los encabezados): se aplica en el SERVIDOR (query
 * params, con debounce) y también en el cliente para feedback instantáneo. El orden
 * es por columna dentro de la tabla y el PAGINADO se resuelve en el servidor
 * (envelope {items, total}). La query se precarga en el loader del route.
 */
export function BandejaAutorizacionPage() {
  const [filtros, setFiltros] = useState<BandejaFiltros>({});
  const [page, setPage] = useState(1);
  // Fila de filtros por columna COLAPSADA por default (mismo patrón que Mis
  // solicitudes / Mis facturas) · el botón "Filtros" la muestra/oculta y trae el contador.
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [filtrosDebounced] = useDebouncedValue(filtros, 350);

  const filtrosActivos = Object.values(filtros).filter((v) => v != null && String(v).trim() !== "").length;

  const serverParams = useMemo(
    () => ({
      ...filtrosNoVacios(filtrosDebounced),
      page,
      pageSize: BANDEJA_PAGE_SIZE,
    }),
    [filtrosDebounced, page],
  );

  // Cambiar los filtros invalida la página actual · siempre de vuelta a la 1.
  useEffect(() => {
    setPage(1);
  }, [filtrosDebounced]);

  const { data, refetch, isFetching, isPlaceholderData } =
    useBandejaAutorizacion(serverParams);
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const { permiso, solicitar } = useNotificarLlegada(total);

  const hayFiltros = Object.keys(filtrosNoVacios(filtrosDebounced)).length > 0;

  // ── Selección para autorización masiva ────────────────────────────────────
  const [seleccionadas, setSeleccionadas] = useState<Set<string>>(new Set());
  // Al cambiar de página o de filtros, limpiamos la selección (es por página).
  useEffect(() => {
    setSeleccionadas(new Set());
  }, [serverParams]);

  const toggleUno = (id: string) =>
    setSeleccionadas((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleVisibles = (ids: string[], marcar: boolean) =>
    setSeleccionadas((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (marcar) next.add(id);
        else next.delete(id);
      }
      return next;
    });

  const seleccionadasItems = useMemo(
    () => items.filter((i) => seleccionadas.has(i.id)),
    [items, seleccionadas],
  );

  // Export a Excel · trae TODAS las páginas con los filtros actuales.
  const obtenerTodas = async (): Promise<BandejaAutorizacionItem[]> => {
    const params = filtrosNoVacios(filtrosDebounced);
    const todas: BandejaAutorizacionItem[] = [];
    for (let p = 1; ; p++) {
      const pagina = await listarBandejaAutorizacion({ ...params, page: p, pageSize: 100 });
      todas.push(...pagina.items);
      if (todas.length >= pagina.total || pagina.items.length === 0) break;
    }
    return todas;
  };

  return (
    <div className="w-full px-4 py-6 md:px-6">
      <BandejaHeader
        permiso={permiso}
        onActivarNotificaciones={solicitar}
        fetching={isFetching}
        onRefrescar={() => void refetch()}
        obtenerFilas={obtenerTodas}
      />
      <Stack gap="md">
        {/* Primera carga: skeleton, NUNCA el estado vacío (con `total` en 0 mientras
            no hay datos se veía «bandeja vacía» por un instante). Al cambiar de
            página/filtro `keepPreviousData` conserva las filas + «Buscando…». */}
        {!data ? (
          <TablaSkeleton
            columnas={COLUMNAS_BANDEJA}
            minWidth={MIN_WIDTH_BANDEJA}
            filas={BANDEJA_PAGE_SIZE}
            conCheckbox
            conToolbar
            movil
            etiqueta="Cargando bandeja…"
          />
        ) : total === 0 && !hayFiltros ? (
          <BandejaVaciaCard />
        ) : (
          <>
            {/* Toolbar STICKY · se mantiene visible bajo el header fijo (56px) al
                hacer scroll. Botón de filtros (con contador) a la izquierda y
                paginación a la derecha · mismo patrón que Mis facturas. */}
            <div className="bg-pagina sticky top-[var(--shell-header-h)] z-20 -mx-4 px-4 py-2 md:-mx-6 md:px-6">
              <Group justify="space-between" align="center" gap="sm" wrap="wrap">
                <Button
                  size="sm"
                  variant={mostrarFiltros ? "light" : "default"}
                  leftSection={<IconFilter size={14} stroke={1.8} />}
                  onClick={() => setMostrarFiltros((v) => !v)}
                  aria-pressed={mostrarFiltros}
                  aria-label={mostrarFiltros ? "Ocultar fila de filtros" : "Mostrar fila de filtros"}
                >
                  Filtros{filtrosActivos > 0 ? ` · ${filtrosActivos}` : ""}
                </Button>
                <PaginadorTabla
                  total={total}
                  page={page}
                  pageSize={BANDEJA_PAGE_SIZE}
                  onPageChange={setPage}
                  variante="inferior"
                  sustantivo="solicitudes"
                />
              </Group>
            </div>
            <BandejaAccionMasiva
              seleccionadas={seleccionadasItems}
              onLimpiar={() => setSeleccionadas(new Set())}
              onAutorizado={() => setSeleccionadas(new Set())}
            />
            <BandejaDesktopTable
              items={items}
              filtros={filtros}
              buscando={isPlaceholderData}
              mostrarFiltros={mostrarFiltros}
              onFiltrar={(key, valor) => setFiltros((prev) => ({ ...prev, [key]: valor }))}
              seleccionadas={seleccionadas}
              onToggleUno={toggleUno}
              onToggleVisibles={toggleVisibles}
            />
            <BandejaMobileList items={items} />
            <PaginadorTabla
              total={total}
              page={page}
              pageSize={BANDEJA_PAGE_SIZE}
              onPageChange={setPage}
              sustantivo="solicitudes"
            />
          </>
        )}
      </Stack>
    </div>
  );
}
